import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, ChevronDown, MoreVertical, CreditCard, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface Invoice {
  id: string;
  date: string;
  amount: string;
  status: string;
  is_subscription?: boolean;
  pdf_url?: string;
  hosted_url?: string;
  due_date?: string | null;
  description?: string;
}

interface InvoiceHistoryCardProps {
  invoices: Invoice[];
  isLoading?: boolean;
}

export const InvoiceHistoryCard: React.FC<InvoiceHistoryCardProps> = ({
  invoices,
  isLoading
}) => {
  useLanguage();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);

  // Verifica se há mais conteúdo para rolar
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        // Se ainda não chegou ao final, mostra o indicador
        setHasMoreContent(scrollHeight > clientHeight && scrollTop + clientHeight < scrollHeight - 10);
      }
    };

    // Aguarda um pouco para garantir que o DOM foi renderizado
    const timeout = setTimeout(checkScroll, 100);

    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', checkScroll);

    return () => {
      clearTimeout(timeout);
      container?.removeEventListener('scroll', checkScroll);
    };
  }, [invoices, isLoading]);

  return (
    <Card
      className="border border-[#E5E7EB] rounded-[14px] overflow-hidden h-full flex flex-col"
      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}
    >
      <div className="p-4 flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-semibold text-[#111827]">{translateInterfaceText("Histórico de Faturas")}</h2>
            {!isLoading && invoices.length > 0 && (
              <span className="text-[12px] text-[#6B7280] bg-[#F3F4F6] px-2.5 py-1 rounded-full font-medium">
                {invoices.length} {invoices.length === 1 ? translateInterfaceText("fatura") : translateInterfaceText("faturas")}
              </span>
            )}
          </div>
          <p className="text-[13px] text-[#6B7280] mt-0.5">
            {invoices.length > 5
              ? translateInterfaceText("Role para ver todas as faturas")
              : translateInterfaceText("Pague faturas em aberto e baixe as anteriores")
            }
          </p>
        </div>
        
        <CardContent className="p-0 flex-1 flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-pulse space-y-3 w-full">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex items-center justify-center p-8 text-gray-500">
              <p className="text-sm">{translateInterfaceText("Nenhuma fatura encontrada")}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-[#E5E7EB] overflow-hidden flex-1 flex flex-col relative">
              {/* Área de scroll com altura fixa para 5 itens */}
              <div
                ref={scrollContainerRef}
                className="overflow-x-auto overflow-y-auto max-h-[340px] custom-scrollbar"
              >
                <Table className="min-w-[500px]">
                  <TableHeader className="sticky top-0 z-10">
                    <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                      <TableHead className="text-[13px] font-semibold text-[#374151] whitespace-nowrap">{translateInterfaceText("Data")}</TableHead>
                      <TableHead className="text-[13px] font-semibold text-[#374151] whitespace-nowrap">{translateInterfaceText("Valor")}</TableHead>
                      <TableHead className="text-[13px] font-semibold text-[#374151] whitespace-nowrap">{translateInterfaceText("Status")}</TableHead>
                      <TableHead className="text-right text-[13px] font-semibold text-[#374151] whitespace-nowrap">{translateInterfaceText("Ações")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice, index) => (
                      <TableRow
                        key={invoice.id}
                        className={cn(
                          "border-b border-[#E5E7EB]",
                          index % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"
                        )}
                      >
                        <TableCell className="text-[14px] text-[#374151]">{invoice.date}</TableCell>
                        <TableCell className="text-[14px] font-semibold text-[#111827]">{invoice.amount}</TableCell>
                        <TableCell>
                          <span className={cn(
                            "inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold",
                            invoice.status === 'paid'
                              ? 'bg-[#EEF4FF] text-[#2563EB]'
                              : invoice.status === 'open'
                              ? 'bg-[#FEF3C7] text-[#B45309]'
                              : 'bg-gray-100 text-gray-600'
                          )}>
                            {invoice.status === 'paid'
                              ? translateInterfaceText("Pago")
                              : invoice.status === 'open'
                              ? translateInterfaceText("Em aberto")
                              : invoice.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] transition-colors"
                              >
                                <MoreVertical className="w-[18px] h-[18px]" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {invoice.status === 'open' && (
                                invoice.is_subscription ? (
                                  <DropdownMenuItem
                                    className="text-[#2563EB] font-medium cursor-pointer"
                                    onClick={() => navigate('/checkout')}
                                  >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    {translateInterfaceText("Reativar plano")}</DropdownMenuItem>
                                ) : invoice.hosted_url ? (
                                  <DropdownMenuItem
                                    className="text-[#2563EB] font-medium cursor-pointer"
                                    onClick={() => window.open(invoice.hosted_url, '_blank', 'noopener,noreferrer')}
                                  >
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    {translateInterfaceText("Pagar agora")}</DropdownMenuItem>
                                ) : null
                              )}
                              <DropdownMenuItem
                                className="cursor-pointer"
                                disabled={!invoice.pdf_url}
                                onClick={() => invoice.pdf_url && window.open(invoice.pdf_url, '_blank')}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                {translateInterfaceText("Baixar fatura")}</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Indicador de mais conteúdo */}
              {hasMoreContent && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none flex items-end justify-center pb-3 z-10">
                  <div className="flex items-center gap-1.5 text-[#2563EB] text-xs font-semibold bg-white px-3 py-1.5 rounded-full shadow-sm border border-[#E5E7EB] animate-pulse">
                    <ChevronDown className="w-3.5 h-3.5" />
                    <span>{translateInterfaceText("Role para ver mais faturas")}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </div>

      {/* Estilos customizados para scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }

        /* Firefox */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #D1D5DB #F3F4F6;
        }
      `}</style>
    </Card>
  );
};